import React, { useEffect, useRef, useState } from 'react';
import { Col, Row } from 'react-bootstrap';
import { FormattedMessage, useIntl } from 'react-intl';
import { useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import './CustomersSearch.scss';
import { v4 as uuid } from 'uuid';

import { routeTo } from '@app/helpers/RoutesHelper';
import { formatSiretNumber } from '@app/helpers/ToolsHelper';

import { RootState } from '@app/store/rootDuck';

import useFilters from '../../../hooks/useFilters';
import { useCustomersContext } from '../../../pages/customers/context/CustomersContext';
import SearchInputFilter from '../../../partials/content/SearchInputFilter';
import ROUTES from '../../../router/Routes';

interface FilterKeys {
    research: string;
    vehicle: string;
    companyName: string;
    siret: string;
    isDmsPro?: string;
}

interface FilterValue {
    value: string | boolean | null;
}

interface Filters {
    research: FilterValue;
    vehicle: FilterValue;
    companyName: FilterValue;
    siret: FilterValue;
    isDmsPro?: FilterValue;
}

interface CustomersSearchProps {
    research: string;
    className: string;
    refresh: (() => void) | undefined;
    redirect: boolean;
    colLg: number;
    withDmsInputs: boolean;
    searchType: 'individual' | 'company';
    additionalNode: React.ReactNode;
}

const generateKeys = (): FilterKeys => ({
    research: uuid(),
    vehicle: uuid(),
    companyName: uuid(),
    siret: uuid(),
});

export const getEmptyFilters = (): Filters => ({
    research: { value: null },
    vehicle: { value: null },
    companyName: { value: null },
    siret: { value: null },
});

const CustomersSearch: React.FC<CustomersSearchProps> = ({
    research = 'customers_search',
    className = '',
    refresh = undefined,
    redirect = false,
    colLg = 4,
    withDmsInputs = false,
    searchType = 'individual',
    additionalNode = null,
}) => {
    const [keys, setKeys] = useState<FilterKeys>(generateKeys());
    const prevSearchTypeRef = useRef<string>(searchType);
    const Intl = useIntl();
    const history = useHistory();
    const stateCustomers = useSelector((state: RootState) => state.customer.customers);
    const { isDmsActive } = useCustomersContext();
    const { updateFilters } = useFilters({
        name: research,
        path: ROUTES.CUSTOMERS.PATH,
        persisted: false,
        initialize: true,
    });

    const redirectOnChange = (): void => {
        history.push(routeTo(ROUTES.CUSTOMERS.PATH));
        setKeys(generateKeys());
    };

    const shouldShowDmsInputs = withDmsInputs && isDmsActive;

    const applyFilters = (fieldName: keyof Filters, value: string | boolean | null): void => {
        const trimmed = typeof value === 'string' ? value?.trim?.() ?? null : value;

        const filters = { ...getEmptyFilters(), [fieldName]: { value: trimmed } };

        if (shouldShowDmsInputs) {
            filters.isDmsPro = { value: searchType === 'company' };
        }

        updateFilters({ filters, page: 1 });

        if (redirect) {
            redirectOnChange();
        }
    };

    // Reset search when searchType changes
    useEffect(() => {
        if (prevSearchTypeRef.current !== searchType) {
            const resetFilters = getEmptyFilters();

            if (shouldShowDmsInputs) {
                resetFilters.isDmsPro = { value: searchType === 'company' };
            }

            updateFilters({ filters: resetFilters, page: 1 });
            setKeys(generateKeys());
        }

        prevSearchTypeRef.current = searchType;
    }, [searchType, shouldShowDmsInputs, updateFilters]);

    return (
        <Row className={`customers-search ${className}`}>
            {(!shouldShowDmsInputs || searchType === 'individual') && (
                <>
                    <Col lg={colLg}>
                        <SearchInputFilter
                            name="research"
                            key={keys.research}
                            placeholder={Intl.formatMessage({ id: 'CUSTOMERS.TABLE.VIEW.RESEARCH.PLACEHOLDER' })}
                            onChange={({ research: researchValue }: { research: FilterValue }) =>
                                applyFilters('research', researchValue?.value)
                            }
                            disabled={stateCustomers.loading}
                            refresh={refresh}
                        />
                    </Col>
                    <div className="pt-1 pl-6 font-weight-bold align-self-center">
                        <FormattedMessage id="TRANSLATOR.OR" />
                    </div>
                    <Col lg={colLg}>
                        <SearchInputFilter
                            name="vehicle"
                            key={keys.vehicle}
                            placeholder={Intl.formatMessage({
                                id: 'CUSTOMERS.TABLE.VIEW.VEHICLE_RESEARCH.PLACEHOLDER',
                            })}
                            onChange={({ vehicle }: { vehicle: FilterValue }) =>
                                applyFilters('vehicle', vehicle?.value)
                            }
                            disabled={stateCustomers.loading}
                            refresh={refresh}
                        />
                    </Col>
                </>
            )}

            {shouldShowDmsInputs && searchType === 'company' && (
                <>
                    <Col lg={colLg}>
                        <SearchInputFilter
                            name="companyName"
                            key={keys.companyName}
                            placeholder={Intl.formatMessage({ id: 'CUSTOMERS.TABLE.VIEW.COMPANY_NAME.PLACEHOLDER' })}
                            onChange={({ companyName }: { companyName: FilterValue }) =>
                                applyFilters('companyName', companyName?.value)
                            }
                            disabled={stateCustomers.loading}
                            refresh={refresh}
                        />
                    </Col>
                    <div className="pt-1 pl-6 font-weight-bold align-self-center">
                        <FormattedMessage id="TRANSLATOR.OR" />
                    </div>
                    <Col lg={colLg}>
                        <SearchInputFilter
                            name="siret"
                            key={keys.siret}
                            placeholder={Intl.formatMessage({ id: 'CUSTOMERS.TABLE.VIEW.SIRET.PLACEHOLDER' })}
                            onChange={({ siret }: { siret: FilterValue }) => applyFilters('siret', siret?.value)}
                            disabled={stateCustomers.loading}
                            refresh={refresh}
                            pattern={/^\d{14}$/}
                            patternErrorMessage={Intl.formatMessage({ id: 'CUSTOMERS.VALIDATION.SIRET.INVALID' })}
                            onInput={(e: React.FormEvent<HTMLInputElement>) => {
                                const input = e.currentTarget;
                                const formatted = formatSiretNumber(input.value);
                                const cleaned = formatted?.replace(/\s+/g, '') || '';
                                input.value = cleaned;
                            }}
                        />
                    </Col>
                </>
            )}

            {additionalNode}
        </Row>
    );
};

export default CustomersSearch;
